/**
 * Return-to-page after login (ASB-4053).
 *
 * Senders put the page the user was on into /login?next=..., and the login page
 * goes there after a successful login. next comes from the URL, so anyone can
 * craft it: only a path on this site is accepted, or a link could send users
 * to an outside site right after they log in (an open redirect).
 */

// Pages that must never be a next target: going "back" to them would loop.
const NO_RETURN_PATHS = ["/login", "/password-recovery"];

// A single leading slash, not "//host" or "/\host": browsers read both as another host.
const SINGLE_SLASH = /^\/(?![/\\])/;

const isNoReturnPath = (pathname) => {
	const path = pathname.toLowerCase().replace(/\/+$/, "");
	return NO_RETURN_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
};

/**
 * The validated next path (pathname + search + hash), or null when next is
 * missing or not a page on this site. Callers fall back to "/".
 */
export const safeNextPath = (next, origin = window.location.origin) => {
	if (typeof next !== "string" || next === "") return null;
	// A single leading slash; not "//host" or "/\host" (browsers read both as
	// another host), and nothing the URL parser strips, such as "/\t/host".
	if (!SINGLE_SLASH.test(next) || /[\\\s\u0000-\u001f\u007f]/.test(next)) return null;
	let url;
	try {
		url = new URL(next, origin);
	} catch (e) {
		return null;
	}
	if (url.origin !== origin || isNoReturnPath(url.pathname)) return null;
	// Check what is returned, not only what came in: parsing resolves dot segments,
	// so "/.//evil.com" (or "/%2e//evil.com") comes out as "//evil.com".
	if (!SINGLE_SLASH.test(url.pathname)) return null;
	return `${url.pathname}${url.search}${url.hash}`;
};

/** A full page load of url. One place, so tests can stand in for it. */
export const navigateTo = (url) => {
	window.location.href = url;
};

/**
 * The login URL that returns the user to `location` afterwards, with any extra
 * query parameters (e.g. reason=session_expired) ahead of next. The dashboard
 * and the no-return pages get no next, since "/" is the default anyway.
 */
export const loginPathFor = (location = window.location, params = {}) => {
	const query = new URLSearchParams(params);
	const here = `${location.pathname}${location.search}${location.hash}`;
	if (location.pathname !== "/" && safeNextPath(here)) {
		query.set("next", here);
	}
	const qs = query.toString();
	return qs ? `/login?${qs}` : "/login";
};
